import {
	Box,
	Drawer,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Tooltip,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { UserContext } from "../../hooks/UserContext";
import { useAirtableStatus } from "../../hooks/useAirtableStatus";
import { AppVersion } from "../helpers";
import { API } from "../../api";
import { User } from "../../models";
import { setJwtToken, setRefreshToken } from "../../utilities/utils";

import LogoutIcon from "@mui/icons-material/Logout";
import InfoIcon from "@mui/icons-material/Info";
import { NotInterested } from "@mui/icons-material";

import "./DrawerNavigation.css";
import { type NavLink, USER_LINKS, LOGIN_LINK, ADMIN_LINKS, DEVELOPER_LINKS } from "./navLinks";
export type { NavLink } from "./navLinks";

export const SIDEBAR_COLLAPSED_WIDTH = 60;

const ItemBreak = React.memo(function ItemBreak() {
	return <ListItem className="sidebar-item-break" />;
});

const STATUS_COLORS: Record<string, string> = {
	none: "#4caf50",
	minor: "#ff9800",
	major: "#f44336",
	critical: "#f44336",
};

function AirtableStatusIndicator({ expanded }: { expanded: boolean }) {
	const { indicator } = useAirtableStatus();
	const color = indicator ? STATUS_COLORS[indicator] : "#9e9e9e";

	return (
		<Tooltip title={expanded ? "" : "Airtable"} placement="right">
			<ListItemButton onClick={() => window.open("https://status.airtable.com", "_blank", "noopener,noreferrer")}>
				<ListItemIcon>
					<Box className="sidebar-status-icon">
						<Box className="sidebar-status-dot" sx={{ backgroundColor: color }} />
					</Box>
				</ListItemIcon>
				<ListItemText primary="Airtable Status" />
			</ListItemButton>
		</Tooltip>
	);
}

function logout() {
	setJwtToken("");
	setRefreshToken("");
	localStorage.clear();
	sessionStorage.clear();
	window.location.href = "/login";
}

interface DrawerNavigationProps {
	onNavigate: (link: NavLink) => void;
	mobileOpen: boolean;
	onMobileClose: () => void;
}

export const DrawerNavigation = (props: DrawerNavigationProps) => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const user = React.useContext(UserContext) as User | undefined;
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("md"));
	const [expanded, setExpanded] = React.useState(false);

	const userLinks = user?.id ? USER_LINKS : [LOGIN_LINK];
	const adminLinks = user?.isAdmin ? ADMIN_LINKS : [];
	// const softwareTesterLinks = user?.isSoftwareTester ? SOFTWARE_TESTER_LINKS : [];
	const developerLinks = user?.isDeveloper ? DEVELOPER_LINKS : [];

	const companyParam = searchParams.get("company");
	const buildNavPath = (path: string) => (companyParam ? `${path}?company=${companyParam}` : path);

	const handleNavClick = (link: NavLink) => {
		props.onNavigate(link);
		navigate(buildNavPath(link.to), { state: { title: link.text } });
		if (isMobile) props.onMobileClose();
	};

	const renderNavItem = (link: NavLink) => (
		<ListItem key={link.text} disablePadding>
			<Tooltip title={expanded ? "" : link.text} placement="right">
				<ListItemButton onClick={() => handleNavClick(link)}>
					<ListItemIcon>{link.icon}</ListItemIcon>
					<ListItemText primary={link.text} />
				</ListItemButton>
			</Tooltip>
		</ListItem>
	);

	const showExpanded = isMobile || expanded;
	const drawerClass = `sidebar-drawer${showExpanded ? " expanded" : ""}`;

	const navList = (
		<List>
			{userLinks.map(renderNavItem)}
			{user?.id && (
				<ListItem key="logout" disablePadding>
					<Tooltip title={showExpanded ? "" : "Logout"} placement="right">
						<ListItemButton
							onClick={() => {
								logout();
								if (isMobile) props.onMobileClose();
							}}
						>
							<ListItemIcon>
								<LogoutIcon />
							</ListItemIcon>
							<ListItemText primary="Logout" />
						</ListItemButton>
					</Tooltip>
				</ListItem>
			)}
			{adminLinks.length > 0 && <ItemBreak />}
			{adminLinks.map(renderNavItem)}
			{/* {softwareTesterLinks.length > 0 && <ItemBreak />} */}
			{/* {softwareTesterLinks.map(renderNavItem)} */}
			{developerLinks.length > 0 && <ItemBreak />}
			{developerLinks.map(renderNavItem)}
			{developerLinks.length > 0 && <ItemBreak />}
			{user?.isAdmin && (
				<>
					<ListItem key="clear-cache" disablePadding>
						<Tooltip title={showExpanded ? "" : "Clear Cache"} placement="right">
							<ListItemButton
								onClick={() => {
									API.Data.clearCache();
									if (isMobile) props.onMobileClose();
								}}
							>
								<ListItemIcon>
									<NotInterested />
								</ListItemIcon>
								<ListItemText primary="Clear Portal Cache" />
							</ListItemButton>
						</Tooltip>
					</ListItem>
					<ListItem key="airtable-status" disablePadding>
						<AirtableStatusIndicator expanded={showExpanded} />
					</ListItem>
				</>
			)}
			<ListItem key="version" className="sidebar-version">
				<ListItemIcon>
					<InfoIcon fontSize="small" />
				</ListItemIcon>
				<ListItemText primary={<AppVersion />} />
			</ListItem>
		</List>
	);

	if (isMobile) {
		return (
			<Drawer
				variant="temporary"
				anchor="left"
				open={props.mobileOpen}
				onClose={props.onMobileClose}
				className="sidebar-drawer expanded sidebar-drawer-mobile"
			>
				{navList}
			</Drawer>
		);
	}

	return (
		<Drawer
			variant="permanent"
			anchor="left"
			className={drawerClass}
			onMouseEnter={() => setExpanded(true)}
			onMouseLeave={() => setExpanded(false)}
		>
			{navList}
		</Drawer>
	);
};
